// src/pages/dashboard/alumnos/FamiliaCRUD.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Table, Input, Button, Space, Typography, message, Modal,
  Form, Row, Col, Tag, Popconfirm, Spin
} from 'antd';
import {
  SearchOutlined, EditOutlined, DeleteOutlined,
  EyeOutlined, TeamOutlined, UserOutlined, ReloadOutlined
} from '@ant-design/icons';
import apiClient from '../../../api/apiClient';
import EditarResponsablesModal from './components/EditarResponsablesModal';

const { Title, Text } = Typography;

const FamiliaCRUD = () => {
  const navigate = useNavigate();
  const [familias, setFamilias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState('');

  // Modal Crear/Editar
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitulo, setModalTitulo] = useState('');
  const [familiaEditando, setFamiliaEditando] = useState(null);
  const [loadingModal, setLoadingModal] = useState(false);
  const [form] = Form.useForm();

  // Modal Editar Responsables
  const [showEditarResponsables, setShowEditarResponsables] = useState(false);

  // Modal Ver Detalle
  const [modalVerVisible, setModalVerVisible] = useState(false);
  const [familiaDetalle, setFamiliaDetalle] = useState(null);
  const [responsablesDetalle, setResponsablesDetalle] = useState([]);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  // Cargar familias
  const cargarFamilias = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/familias');
      let lista = res.data.data || res.data;
      if (!Array.isArray(lista)) {
        lista = Object.values(lista);
      }
      setFamilias(lista);
    } catch (error) {
      message.error('Error al cargar familias');
      setFamilias([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarFamilias();
  }, [cargarFamilias]);

  // Filtrar familias
  const familiasFiltradas = familias.filter(f => {
    if (!busqueda.trim()) return true;
    const term = busqueda.toLowerCase();
    return (
      (f.NombreFamilia || '').toLowerCase().includes(term) ||
      (f.NombreRecibo || '').toLowerCase().includes(term) ||
      String(f.IdFamilia || '').includes(busqueda)
    );
  });

  // Abrir modal para editar
  const handleEditar = async (familia) => {
    setFamiliaEditando(familia);
    setModalTitulo('Editar Familia');
    setLoadingModal(true);
    setModalVisible(true);

    try {
      const res = await apiClient.get(`/familias/${familia.IdFamilia}`);
      const datos = res.data.data || res.data;
      form.setFieldsValue({
        NombreFamilia: datos.NombreFamilia || familia.NombreFamilia || '',
        NombreRecibo: datos.NombreRecibo || '',
        TelefonoContacto: datos.TelefonoContacto || '',
        EmailContacto: datos.EmailContacto || '',
        DireccionRecibo: datos.DireccionRecibo || '',
      });
    } catch (error) {
      form.setFieldsValue({
        NombreFamilia: familia.NombreFamilia || '',
      });
    } finally {
      setLoadingModal(false);
    }
  };

  // Guardar (crear o editar)
  const handleGuardar = async () => {
    try {
      const values = await form.validateFields();
      const userFromStorage = JSON.parse(localStorage.getItem('user') || '{}');
      const IdColaborador = userFromStorage.IdUsuario;

      if (!IdColaborador) {
        message.error('No se encontró el usuario en sesión.');
        return;
      }

      setLoadingModal(true);

      if (familiaEditando) {
        // Editar
        await apiClient.put(`/familias/${familiaEditando.IdFamilia}`, {
          ...values,
          IdColaborador,
        });
        message.success('Familia actualizada correctamente');
      } else {
        // Crear
        await apiClient.post('/familias', {
          ...values,
          IdColaborador,
        });
        message.success('Familia creada correctamente');
      }

      setModalVisible(false);
      form.resetFields();
      await cargarFamilias();
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.message || 'Error al guardar';
      message.error(msg);
    } finally {
      setLoadingModal(false);
    }
  };

  // Eliminar/Desactivar
  const handleEliminar = async (familia) => {
    try {
      const userFromStorage = JSON.parse(localStorage.getItem('user') || '{}');
      const IdColaborador = userFromStorage.IdUsuario;

      // Obtener IdUsuario de la familia
      const familiaRes = await apiClient.get(`/familias/${familia.IdFamilia}`);
      const datosFamilia = familiaRes.data.data || familiaRes.data;
      const IdUsuarioFamilia = datosFamilia.IdUsuario;

      // Eliminar/desactivar la familia
      await apiClient.delete(`/familias/${familia.IdFamilia}`, {
        data: { IdColaborador }
      });

      // Desactivar el usuario de la familia si existe
      if (IdUsuarioFamilia) {
        try {
          await apiClient.delete(`/usuarios/${IdUsuarioFamilia}`, {
            data: { IdColaborador }
          });
        } catch (errorUsuario) {
          message.warning('La familia se eliminó pero no se pudo desactivar el usuario asociado');
        }
      }

      message.success(`Familia "${familia.NombreFamilia}" eliminada correctamente`);
      await cargarFamilias();
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.message || 'Error al eliminar familia';
      message.error(msg);
    }
  };

  // Ver detalle
  const handleVer = async (familia) => {
    setFamiliaDetalle(familia);
    setModalVerVisible(true);
    setLoadingDetalle(true);
    setResponsablesDetalle([]);

    try {
      // Cargar datos completos de la familia
      const familiaRes = await apiClient.get(`/familias/${familia.IdFamilia}`);
      const datosCompletos = familiaRes.data.data || familiaRes.data;
      setFamiliaDetalle({ ...familia, ...datosCompletos });

      // Cargar responsables
      const respRes = await apiClient.get(`/responsables/familia/${familia.IdFamilia}`);
      const lista = Array.isArray(respRes.data) ? respRes.data : respRes.data.data || [];
      setResponsablesDetalle(lista);
    } catch (error) {
      // Si falla responsables no pasa nada
    } finally {
      setLoadingDetalle(false);
    }
  };

  const columnas = [
    {
      title: '#',
      render: (_, __, i) => i + 1,
      width: 60,
      align: 'center',
    },
    {
      title: 'ID',
      dataIndex: 'IdFamilia',
      width: 70,
      align: 'center',
      render: (text) => <Tag color="purple">{text}</Tag>
    },
    {
      title: 'Nombre de Familia',
      dataIndex: 'NombreFamilia',
      width: 250,
    },
    {
      title: 'Nombre Recibo',
      dataIndex: 'NombreRecibo',
      width: 250,
      render: (text) => text || '-'
    },
    {
      title: 'Teléfono',
      dataIndex: 'TelefonoContacto',
      width: 140,
      render: (text) => text || '-'
    },
    {
      title: 'Acciones',
      width: 250,
      align: 'center',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleVer(record)}
          >
            Ver
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditar(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Eliminar esta familia?"
            description={`Se eliminará "${record.NombreFamilia}". Esta acción no se puede deshacer.`}
            onConfirm={() => handleEliminar(record)}
            okText="Sí, eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>
        <TeamOutlined /> Gestión de Familias
      </Title>
      <Text type="secondary">Administrar las familias del sistema</Text>

      <Card style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', gap: 16, marginBottom: 24, alignItems: 'center' }}>
          <Input
            placeholder="Buscar por nombre de familia, recibo o ID..."
            prefix={<SearchOutlined />}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            size="large"
            style={{ flex: 1 }}
            allowClear
          />
          <Button
            icon={<ReloadOutlined />}
            size="large"
            onClick={cargarFamilias}
            loading={loading}
          >
            Recargar
          </Button>
        </div>

        <Table
          columns={columnas}
          dataSource={familiasFiltradas}
          rowKey="IdFamilia"
          loading={loading}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            pageSizeOptions: ['10', '15', '25', '50', '100'],
            showTotal: (total) => `Total: ${total} familias`
          }}
          scroll={{ x: 1100 }}
          bordered
        />
      </Card>

      {/* Modal Crear / Editar */}
      <Modal
        title={modalTitulo}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setShowEditarResponsables(false);
        }}
        footer={null}
        width={700}
        destroyOnClose
      >
        {loadingModal ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Form form={form} layout="vertical">
            <Form.Item
              label="Nombre de la Familia"
              name="NombreFamilia"
              rules={[{ required: true, message: 'El nombre de la familia es requerido' }]}
            >
              <Input placeholder="Ej: Pérez López" style={{ fontSize: 16 }} />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Nombre para Recibo" name="NombreRecibo">
                  <Input placeholder="Ej: Juan Pérez" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Teléfono de Contacto" name="TelefonoContacto">
                  <Input placeholder="4444-5555" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Email" name="EmailContacto">
                  <Input placeholder="familia@email.com" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Dirección" name="DireccionRecibo">
                  <Input placeholder="Dirección de la familia" />
                </Form.Item>
              </Col>
            </Row>

            {familiaEditando && (
              <div style={{ textAlign: 'center', margin: '16px 0' }}>
                <Button
                  type="dashed"
                  icon={<TeamOutlined />}
                  onClick={() => setShowEditarResponsables(true)}
                >
                  Editar Responsables
                </Button>
              </div>
            )}

            <div style={{ textAlign: 'right', marginTop: 16 }}>
              <Button onClick={() => { setModalVisible(false); form.resetFields(); }} style={{ marginRight: 12 }}>
                Cancelar
              </Button>
              <Button type="primary" onClick={handleGuardar} loading={loadingModal}>
                {familiaEditando ? 'Guardar Cambios' : 'Crear Familia'}
              </Button>
            </div>
          </Form>
        )}

        {/* Modal Editar Responsables */}
        <EditarResponsablesModal
          open={showEditarResponsables}
          onCancel={() => setShowEditarResponsables(false)}
          idFamilia={familiaEditando?.IdFamilia}
        />
      </Modal>

      {/* Modal Ver Detalle */}
      <Modal
        title={
          <Space>
            <TeamOutlined />
            <span>Detalle de Familia: {familiaDetalle?.NombreFamilia}</span>
          </Space>
        }
        open={modalVerVisible}
        onCancel={() => setModalVerVisible(false)}
        footer={
          <Button onClick={() => setModalVerVisible(false)}>Cerrar</Button>
        }
        width={800}
        destroyOnClose
      >
        {loadingDetalle ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : familiaDetalle ? (
          <>
            {/* Datos de la familia */}
            <Card title="Datos de la Familia" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 12]}>
                <Col span={12}>
                  <Text type="secondary">ID Familia:</Text>
                  <div><Tag color="purple">{familiaDetalle.IdFamilia}</Tag></div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Nombre Familia:</Text>
                  <div><Text strong>{familiaDetalle.NombreFamilia || '-'}</Text></div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Nombre Recibo:</Text>
                  <div><Text>{familiaDetalle.NombreRecibo || '-'}</Text></div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Teléfono:</Text>
                  <div><Text>{familiaDetalle.TelefonoContacto || '-'}</Text></div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Email:</Text>
                  <div><Text>{familiaDetalle.EmailContacto || '-'}</Text></div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Dirección:</Text>
                  <div><Text>{familiaDetalle.DireccionRecibo || '-'}</Text></div>
                </Col>
              </Row>
            </Card>

            {/* Responsables */}
            <Card
              title={
                <span>
                  <UserOutlined /> Responsables ({responsablesDetalle.length})
                </span>
              }
              size="small"
            >
              {responsablesDetalle.length > 0 ? (
                responsablesDetalle.map((r, index) => (
                  <div
                    key={r.IdResponsable || index}
                    style={{
                      padding: 12,
                      marginBottom: 8,
                      background: r.EsResponsable ? '#e6f7ff' : '#fafafa',
                      borderRadius: 6,
                      border: r.EsResponsable ? '1px solid #91d5ff' : '1px solid #f0f0f0'
                    }}
                  >
                    <Row gutter={16} align="middle">
                      <Col span={8}>
                        <Text strong>{r.NombreResponsable}</Text>
                      </Col>
                      <Col span={4}>
                        <Tag color="geekblue">{r.TipoResponsable || 'Responsable'}</Tag>
                      </Col>
                      <Col span={5}>
                        <Text type="secondary">DPI: </Text>{r.DPI || '-'}
                      </Col>
                      <Col span={4}>
                        <Text type="secondary">NIT: </Text>{r.NIT || '-'}
                      </Col>
                      <Col span={3}>
                        {r.EsResponsable && <Tag color="blue">Principal</Tag>}
                      </Col>
                    </Row>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', color: '#999', padding: 16 }}>
                  Esta familia no tiene responsables registrados
                </div>
              )}
            </Card>
          </>
        ) : null}
      </Modal>

      {/* Botón regresar */}
      <div style={{ marginTop: 40, textAlign: 'center', paddingBottom: 24 }}>
        <Button
          size="large"
          onClick={() => navigate('/dashboard')}
          style={{ minWidth: 200 }}
        >
          Regresar al Dashboard
        </Button>
      </div>
    </div>
  );
};

export default FamiliaCRUD;
